import { createSlice, createAction } from '@reduxjs/toolkit';

// --- Toggle Product Status Actions ---
export const bookNowStatusChangeRequest = createAction('product/toggleStatusRequest');
export const bookNowStatusChange = createAction('product/toggleStatusSuccess');
export const bookNowStatusChangeFailure = createAction('product/toggleStatusFailure');

// --- Delete Product Actions ---
export const deleteProductRequest = createAction('product/deleteRequest');
export const deleteProductSuccess = createAction('product/deleteSuccess');
export const deleteProductFailure = createAction('product/deleteFailure');

// --- Add Product Actions ---
export const addProductRequest = createAction('product/addRequest');
export const addProductSuccess = createAction('product/addSuccess');
export const addProductFailure = createAction('product/addFailure');

// --- Edit Product Actions ---
export const editProductRequest = createAction('product/editRequest');
export const editProductSuccess = createAction('product/editSuccess');
export const editProductFailure = createAction('product/editFailure');

const initialState = {
  data: [],
  /** From GET /getproducts: { tconline, tcoffline } */
  productTermsAndConditions: null,
  loading: false,
  error: null,
  message: '',

  singleProduct: null,
  singleLoading: false,
  singleError: null,

  toggleStatusSuccess: false,
  toggleStatusFailure: false,

  deleteLoading: false,
  deleteSuccess: false,
  deleteFailure: false,

  addLoading: false,
  addSuccess: false,
  addFailure: false,

  editLoading: false,
  editSuccess: false,
  editFailure: false,
};

const productListSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    fetchProductListRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchProductListSuccess: (state, action) => {
      state.loading = false;
      const payload = action.payload;
      if (Array.isArray(payload)) {
        state.data = payload;
        state.productTermsAndConditions = null;
      } else {
        state.data = payload?.productlist ?? [];
        state.productTermsAndConditions =
          payload?.producttermsandconditions ?? null;
      }
      state.message = 'Product list fetched successfully';
    },
    fetchProductListFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    fetchSingleProductRequest: (state) => {
      state.singleLoading = true;
      state.singleError = null;
    },
    fetchSingleProductSuccess: (state, action) => {
      state.singleLoading = false;
      state.singleProduct = action.payload;
    },
    fetchSingleProductFailure: (state, action) => {
      state.singleLoading = false;
      state.singleError = action.payload;
    },

    resetToggleStatusFlags: (state) => {
      state.toggleStatusSuccess = false;
      state.toggleStatusFailure = false;
    },

    resetDeleteFlags: (state) => {
      state.deleteSuccess = false;
      state.deleteFailure = false;
    },

    resetAddEditFlags: (state) => {
      state.addSuccess = false;
      state.addFailure = false;
      state.editSuccess = false;
      state.editFailure = false;
    },

    clearLoading: (state) => {
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Toggle Status
      .addCase(bookNowStatusChangeRequest, (state) => {
        state.loading = true;
        state.toggleStatusSuccess = false;
        state.toggleStatusFailure = false;
      })
      .addCase(bookNowStatusChange, (state) => {
        state.loading = false;
        state.toggleStatusSuccess = true;
      })
      .addCase(bookNowStatusChangeFailure, (state) => {
        state.loading = false;
        state.toggleStatusFailure = true;
      })

      // Delete Product
      .addCase(deleteProductRequest, (state) => {
        state.deleteLoading = true;
        state.deleteSuccess = false;
        state.deleteFailure = false;
      })
      .addCase(deleteProductSuccess, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccess = true;
        state.data = state.data.filter(item => item._id !== action.payload);
      })
      .addCase(deleteProductFailure, (state, action) => {
        state.deleteLoading = false;
        state.deleteFailure = true;
        state.error = action.payload;
      })

      // Add Product
      .addCase(addProductRequest, (state) => {
        state.addLoading = true;
        state.addSuccess = false;
        state.addFailure = false;
      })
      .addCase(addProductSuccess, (state, action) => {
        state.addLoading = false;
        state.addSuccess = true;
        // state.data.unshift(action.payload); // Optional: add to beginning of list
      })
      .addCase(addProductFailure, (state, action) => {
        state.addLoading = false;
        state.addFailure = true;
        state.error = action.payload;
      })

      // Edit Product
      .addCase(editProductRequest, (state) => {
        state.editLoading = true;
        state.editSuccess = false;
        state.editFailure = false;
      })
      .addCase(editProductSuccess, (state, action) => {
        state.editLoading = false;
        state.editSuccess = true;
        // Optional: update edited product in data array
        const index = state.data.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editProductFailure, (state, action) => {
        state.editLoading = false;
        state.editFailure = true;
        state.error = action.payload;
      });
  },
});

export const {
  fetchProductListRequest,
  fetchProductListSuccess,
  fetchProductListFailure,
  fetchSingleProductRequest,
  fetchSingleProductSuccess,
  fetchSingleProductFailure,
  resetToggleStatusFlags,
  resetDeleteFlags,
  resetAddEditFlags,
  clearLoading,
} = productListSlice.actions;

export default productListSlice.reducer;
